/**
 * AI Assistant — scope resolution (G1).
 *
 * The assistant works in one of two scopes (COPILOT_API_CONTRACT.md, G1 addendum):
 *   { type: 'company' }            — every job the actor may access
 *   { type: 'job', job_id: 1234 }  — one job in depth
 *
 * The scope is derived from the route (a `jobId` search param or
 * `/hirewithus/my-jobs/:jobId` → job scope, anything else → company) unless
 * the HR pinned one from the header switcher. A pin is stored in
 * localStorage('copilot.scope') together with the route job that was on
 * screen when it was made; it survives until the HR picks another scope or
 * navigates to a *different* job page (a route-derived job only beats a pin
 * when the job changes).
 *
 * Pure functions only — no React — so they are trivial to unit test.
 */

export const SCOPE_STORAGE_KEY = 'copilot.scope';
export const COMPANY_SCOPE = Object.freeze({ type: 'company' });
export const COMPANY_LABEL = 'All jobs';

const MY_JOBS_JOB_RE = /^\/hirewithus\/my-jobs\/([^/?#]+)/i;

function normaliseJobId(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const str = String(raw).trim();
  if (!str) return null;
  const num = Number(str);
  return Number.isFinite(num) ? num : str;
}

/** Build a job scope; returns the company scope when `jobId` is empty. */
export function jobScope(jobId) {
  const id = normaliseJobId(jobId);
  return id === null ? COMPANY_SCOPE : { type: 'job', job_id: id };
}

/** Validate / normalise anything that claims to be a scope. */
export function normaliseScope(scope) {
  if (!scope || typeof scope !== 'object') return COMPANY_SCOPE;
  if (scope.type === 'job') return jobScope(scope.job_id ?? scope.jobId);
  return COMPANY_SCOPE;
}

/** 'company' | 'job:<id>' — stable key for caches and storage. */
export function scopeKey(scope) {
  const s = normaliseScope(scope);
  return s.type === 'job' ? `job:${s.job_id}` : 'company';
}

export function scopesEqual(a, b) {
  return scopeKey(a) === scopeKey(b);
}

/** Query params for GET endpoints: `{ scope: 'company' }` or `{ job_id }`. */
export function scopeParams(scope) {
  const s = normaliseScope(scope);
  return s.type === 'job' ? { job_id: s.job_id } : { scope: 'company' };
}

/** Job id carried by a route (search param `jobId` or `/my-jobs/:jobId`), else null. */
export function routeJobId(location) {
  if (!location) return null;
  const pathname = String(location.pathname || '');
  const search = String(location.search || '');
  try {
    const params = new URLSearchParams(search);
    const fromQuery = normaliseJobId(params.get('jobId') ?? params.get('job_id'));
    if (fromQuery !== null) return fromQuery;
  } catch {
    // malformed search string — ignore
  }
  const match = MY_JOBS_JOB_RE.exec(pathname);
  if (match) {
    try {
      return normaliseJobId(decodeURIComponent(match[1]));
    } catch {
      return normaliseJobId(match[1]);
    }
  }
  return null;
}

/** Scope implied by the route alone. */
export function routeScope(location) {
  return jobScope(routeJobId(location));
}

/**
 * Route name for `ui_context.page`:
 * overview, my-jobs, applications, hiring-lifecycle, hired-profiles, messages,
 * billing, settings, postjob, other.
 */
export function pageFromRoute(location) {
  if (!location) return 'other';
  const path = String(location.pathname || '').toLowerCase().replace(/\/+$/, '');
  const jobId = routeJobId(location);
  if (path === '/hirewithus/overview') return jobId !== null ? 'applications' : 'overview';
  if (path === '/hirewithus/my-jobs') return 'my-jobs';
  if (path.startsWith('/hirewithus/my-jobs/')) return 'applications';
  if (path === '/hirewithus/applications') return 'applications';
  if (path.startsWith('/hirewithus/hiring-lifecycle')) return 'hiring-lifecycle';
  if (path.startsWith('/hirewithus/hired-profiles')) return 'hired-profiles';
  if (path.startsWith('/hirewithus/messages')) return 'messages';
  if (path.startsWith('/hirewithus/billing') || path.startsWith('/hirewithus/plans')) return 'billing';
  if (path.startsWith('/hirewithus/settings')) return 'settings';
  if (path.startsWith('/hirewithus/postjob')) return 'postjob';
  return 'other';
}

export const PAGE_LABELS = Object.freeze({
  overview: 'Overview',
  'my-jobs': 'My Jobs',
  applications: 'Applications',
  'hiring-lifecycle': 'Hiring Lifecycle',
  'hired-profiles': 'Hired',
  messages: 'Messages',
  billing: 'Billing',
  settings: 'Settings',
  postjob: 'Post a Job',
  other: 'Dashboard',
});

export function pageLabel(page) {
  return PAGE_LABELS[page] || PAGE_LABELS.other;
}

// ---------------------------------------------------------------------------
// pinned scope (localStorage)
// ---------------------------------------------------------------------------

function safeStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {
    // storage blocked
  }
  return null;
}

/**
 * Read the pinned scope: `{ scope, routeJobId, title }` or null.
 * `routeJobId` is the route's job when the pin was made (null when none).
 */
export function readPinnedScope() {
  const store = safeStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(SCOPE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      scope: normaliseScope(parsed.scope),
      routeJobId: normaliseJobId(parsed.routeJobId ?? parsed.route_job_id),
      title: typeof parsed.title === 'string' ? parsed.title : null,
    };
  } catch {
    return null;
  }
}

/** Persist a manual pick (`null` clears the pin). */
export function writePinnedScope(pin) {
  const store = safeStorage();
  if (!store) return;
  try {
    if (!pin) store.removeItem(SCOPE_STORAGE_KEY);
    else
      store.setItem(
        SCOPE_STORAGE_KEY,
        JSON.stringify({
          scope: normaliseScope(pin.scope),
          routeJobId: normaliseJobId(pin.routeJobId),
          title: pin.title || null,
        })
      );
  } catch {
    // storage unavailable
  }
}

/**
 * Decide the effective scope.
 *
 * @param {object} location  react-router location (or {pathname, search})
 * @param {object|null} pin  result of readPinnedScope()
 * @returns {{ scope: object, pinned: boolean, pinStale: boolean }}
 *   `pinStale` is true when the route moved to a different job and the pin
 *   should be cleared by the caller.
 */
export function resolveScope(location, pin) {
  const fromRoute = routeScope(location);
  const routeJob = fromRoute.type === 'job' ? fromRoute.job_id : null;
  if (!pin) return { scope: fromRoute, pinned: false, pinStale: false };
  const pinnedOnThisJob = routeJob === null || String(routeJob) === String(pin.routeJobId);
  if (pinnedOnThisJob) return { scope: pin.scope, pinned: true, pinStale: false };
  // The HR navigated to a different job page: the route wins, the pin is stale.
  return { scope: fromRoute, pinned: false, pinStale: true };
}

/** Human label for a scope given the switcher's job list. */
export function scopeLabel(scope, jobs = [], fallback = 'This job') {
  const s = normaliseScope(scope);
  if (s.type !== 'job') return COMPANY_LABEL;
  const hit = (jobs || []).find((j) => String(j.job_id ?? j.id) === String(s.job_id));
  return hit?.title || hit?.job_title || fallback;
}
